import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { isVersionLessThan } from "./versionUtils";
import { config } from "./config";
import { apiClient } from "@/shared/apiClient";

export interface AppVersionConfig {
  minVersion: string;
  androidStoreUrl?: string;
}

export interface ForceUpgradeResult {
  upgradeRequired: boolean;
  currentVersion: string;
  minVersion: string;
  storeUrl: string;
}

const DEFAULT_ANDROID_STORE_URL = config.app.androidStoreUrl;

/**
 * Fetches minimum required version from the API.
 * Expected API: GET /api/app/config or GET /api/app/min-version
 * Response: { minVersion: string, androidStoreUrl?: string }
 * The endpoint should be public (no auth) so old app versions can still receive the requirement.
 */
async function fetchMinVersionFromApi(): Promise<AppVersionConfig | null> {
  try {
    const response = await apiClient.get("/api/app/config");
    const minVersion = response?.minVersion;
    if (typeof minVersion === "string" && minVersion.length > 0) {
      return {
        minVersion,
        androidStoreUrl: response?.androidStoreUrl ?? DEFAULT_ANDROID_STORE_URL,
      };
    }
  } catch {
    // API may not exist yet or may be unavailable
  }
  return null;
}

/**
 * Checks if the current native Android app must be upgraded before use.
 * Returns result with upgradeRequired, currentVersion, minVersion, and storeUrl.
 * On non-Android or web, returns upgradeRequired: false.
 */
export async function checkForceUpgrade(): Promise<ForceUpgradeResult> {
  const currentVersion = config.app.version;
  const fallbackStoreUrl = DEFAULT_ANDROID_STORE_URL;

  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "android") {
    return {
      upgradeRequired: false,
      currentVersion,
      minVersion: currentVersion,
      storeUrl: fallbackStoreUrl,
    };
  }

  let appVersion = currentVersion;
  try {
    const info = await App.getInfo();
    if (info?.version) {
      appVersion = info.version;
    }
  } catch {
    // Use config version if native API fails
  }

  const fromApi = await fetchMinVersionFromApi();
  const minVersion = fromApi?.minVersion ?? config.app.minVersion;
  const storeUrl = fromApi?.androidStoreUrl ?? fallbackStoreUrl;

  if (!minVersion) {
    return {
      upgradeRequired: false,
      currentVersion: appVersion,
      minVersion: appVersion,
      storeUrl,
    };
  }

  const upgradeRequired = isVersionLessThan(appVersion, minVersion);

  return {
    upgradeRequired,
    currentVersion: appVersion,
    minVersion,
    storeUrl,
  };
}

/** When `VITE_PREVIEW_FORCE_UPGRADE` is `"true"` or `"1"`, use this to render the force-upgrade UI locally. Remove the env var when finished. */
export function getPreviewForceUpgradeResult(): ForceUpgradeResult | null {
  const flag = import.meta.env.VITE_PREVIEW_FORCE_UPGRADE;
  if (flag !== "true" && flag !== "1") return null;
  return {
    upgradeRequired: true,
    currentVersion: config.app.version,
    minVersion: "9.9.9",
    storeUrl: DEFAULT_ANDROID_STORE_URL,
  };
}
